import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  cacheKeys,
  computeCommonGroups,
  computeGroupChanges,
  FeatureName,
  Group,
  PermissionValues,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
  useRoutineFilter,
  useWorkspaceRoutine,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  Colors,
  ColumnData,
  ListCountProvider,
  SelectionStatus,
  SlideLayout,
  Spacing,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useMatch, useNavigate } from 'react-router-dom';

import { ModalButton } from '../../shared/components/modalButton';
import { buildRoutinesListFilter } from './buildRoutinesListFilter';
import { CopyToWorkspaceButton } from './copyToWorkspaceButton';
import { DeleteRoutineButton } from './deleteRoutineButton';
import { EditRoutine } from './editRoutine';
import { NewRoutine } from './newRoutine';
import { RoutineAssetsForm } from './routineAssetsForm';
import { RoutinePreviewColumn } from './routinePreviewColumn';
import { Routines as RoutinesBaseOld } from './routines-old';
import { RoutinesFilters } from './routinesFilters';
import { RoutineList } from './routinesList';
import { RoutinesSelectionPanelMain } from './routinesSelectionPanel';

export const Routines = () => {
  const isCropCircleEnable = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  // When the flag is off, /new-routines/:id/edit and /new-routines/new/:type
  // used to be standalone routes. We dispatch them here so the routes file
  // doesn't need specific entries that would break panelization animations
  // when the flag is on.
  const editMatch = useMatch('/new-routines/:routineId/edit');
  const newMatch = useMatch('/new-routines/new/:type');

  if (isCropCircleEnable) {
    return <RoutinesBase />;
  }

  if (editMatch?.params.routineId) {
    return <EditRoutine routineId={editMatch.params.routineId} />;
  }

  if (newMatch?.params.type) {
    return <NewRoutine type={newMatch.params.type} />;
  }

  return <RoutinesBaseOld />;
};

export const RoutinesBase = ({
  rightColumn: rightColumnOverride,
  thirdColumn: thirdColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  thirdColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const { data: routineFilter } = useRoutineFilter();
  const { account } = useCurrentAccount();

  const previewMatch = useMatch('/new-routines/:routineId/preview');
  const editMatch = useMatch('/new-routines/:routineId/edit');
  const newMatch = useMatch('/new-routines/new/:type');
  const activeRoutineId =
    previewMatch?.params.routineId ?? editMatch?.params.routineId;

  type SelectedRoutineRow = {
    id: string;
    groups?: Array<{ id: string; name: string }>;
  };

  const [rowsSelected, setRowsSelected] = useState<SelectedRoutineRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const impersonatingWorkspace = useCurrentWorkspace(true);
  const currentWorkspace = useCurrentWorkspace();
  const workspace = useCurrentWorkspace(true);
  const currentWorkspaceId = workspace?.id;

  const showCheckboxColumn = useMemo(
    () => !!impersonatingWorkspace,
    [impersonatingWorkspace]
  );

  const filter = useMemo(() => {
    return buildRoutinesListFilter(routineFilter, impersonatingWorkspace?.id);
  }, [routineFilter, impersonatingWorkspace?.id]);

  const updateRowsSelected = useCallback((rows: Array<any>) => {
    setRowsSelected(rows.map((row) => ({ id: row.id, groups: row.groups })));
  }, []);

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const selectedIdsSet = useMemo(
    () => new Set(rowsSelectedIds),
    [rowsSelectedIds]
  );
  const isRowSelected = useCallback(
    (row: { id?: string }) => (row?.id ? selectedIdsSet.has(row.id) : false),
    [selectedIdsSet]
  );

  const { groups: availableGroups } = useAllWorkspaceGroups(workspace?.id);

  const { mutateAsync: updateRoutinesGroups } = useCustomMutation({
    query: UPDATE_ROUTINES_GROUPS,
  });

  // Pre-select groups common to every selected routine.
  // Uses computeCommonGroups to intersect group names across all selected rows,
  // then re-hydrates real IDs from `availableGroups`.
  // - 0 selected → clear selection.
  // - 1 selected → that routine's groups.
  // - N selected → only groups present on every selected routine.
  useEffect(() => {
    if (!hasSelection) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = computeCommonGroups(
      rowsSelected,
      (row) => row.groups ?? [],
      availableGroups
    ).map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups, hasSelection]);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const { added, removed } = computeGroupChanges(selectedGroups, nextGroups);

      setSelectedGroups(nextGroups);

      if (!currentWorkspaceId) {
        return;
      }

      await updateRoutinesGroups({
        input: {
          addedGroups: added.map((g) => ({ id: g.id })),
          removedGroups: removed.map((g) => ({ id: g.id })),
          routineIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: cacheKeys('routine').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      updateRoutinesGroups,
      queryClient,
    ]
  );

  const canViewRoutines = useMemo(
    () =>
      !!(account && account.hasPermission(PermissionValues.routinespage.read)),
    [account]
  );

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const AssetsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assetsCount)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          return (
            <RoutineAssetsForm
              routineId={data.routineId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              onComplete={onComplete}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const AddonsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="shape-outline"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.addonsCount ?? 0)}
          </View>
        }
        modalComponent={({ onComplete }: { onComplete: () => void }) => {
          return (
            <RoutineAssetsForm
              routineId={data.routineId}
              title={data.name}
              showCheckboxColumn={showCheckboxColumn}
              onComplete={onComplete}
              initialTab="addons"
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const handleRoutineRowClick = (row: any) => {
    if (!row?.id) return;
    navigate(`/new-routines/${row.id}/preview`);
  };

  const filterComponent = <RoutinesFilters />;
  const [count, setCount] = useState<number>();

  const columnComponent = (
    <View style={styles.container}>
      <RoutineList
        filter={filter}
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        showCheckboxColumn={showCheckboxColumn}
        showImpersonateWorkspace={canViewRoutines}
        assetsButton={AssetsButton}
        addonsButton={
          currentWorkspace?.type === WorkspaceType.Oem
            ? AddonsButton
            : undefined
        }
        onRowClick={handleRoutineRowClick}
        isProducerWorkspace={currentWorkspace?.type === WorkspaceType.Producer}
        onItemsCountChange={setCount}
      />
    </View>
  );

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection || !workspace?.id) return undefined;

    const title =
      rowsSelected.length === 1
        ? t('routine.rowHeader.routineSelected')
        : t('routine.rowHeader.routinesSelected', {
            count: rowsSelected.length,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <RoutinesSelectionPanelMain
          routineIds={rowsSelectedIds}
          workspaceId={workspace.id}
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
        />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    rowsSelected,
    rowsSelectedIds,
    workspace?.id,
    selectedGroups,
    handleGroupsChange,
    createGroup,
    clearSelection,
    t,
    i18n,
  ]);

  const routeRightColumn = useMemo<ColumnData | undefined>(() => {
    if (activeRoutineId) {
      return {
        title: 'routine.list.routines',
        mainComponent: <RoutinePreviewColumn routineId={activeRoutineId} />,
        headerComponent: editMatch ? undefined : (
          <PreviewHeaderActions routineId={activeRoutineId} />
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => navigate('/new-routines'),
      };
    }

    if (newMatch) {
      return {
        title: 'routines.new.title',
        mainComponent: (
          <View style={styles.editContainer}>
            <NewRoutine type={newMatch.params.type} />
          </View>
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => navigate('/new-routines'),
      };
    }

    return undefined;
  }, [activeRoutineId, editMatch, newMatch, navigate]);

  const routeThirdColumn = useMemo<ColumnData | undefined>(() => {
    if (!editMatch || !activeRoutineId) return undefined;

    return {
      title: 'routines.edit.title',
      mainComponent: (
        <View style={styles.editContainer}>
          <EditRoutine routineId={activeRoutineId} />
        </View>
      ),
      showBackButton: true,
      disableScroll: false,
      onBackButtonPress: () =>
        navigate(`/new-routines/${activeRoutineId}/preview`),
    };
  }, [editMatch, activeRoutineId, navigate]);

  const resolvedRightColumn =
    selectionRightColumn ?? rightColumnOverride ?? routeRightColumn;
  const resolvedThirdColumn = routeThirdColumn ?? thirdColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride || routeRightColumn
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={{
          title: 'routine.list.routines',
          count,
          mainComponent: columnComponent,
          headerComponent: filterComponent,
          disableScroll: true,
        }}
        rightColumn={resolvedRightColumn}
        thirdColumn={resolvedThirdColumn}
        viewMode={resolvedViewMode}
      />
    </ListCountProvider>
  );
};

const PreviewHeaderActions = ({ routineId }: { routineId: string }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const routine = useWorkspaceRoutine(routineId);

  const isGlobalAdmin = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  if (!isGlobalAdmin || !routine) {
    return null;
  }

  return (
    <View style={headerStyles.headerActions}>
      <CopyToWorkspaceButton
        workspaceRoutine={routine}
        onSelected={() => navigate('/new-routines')}
      />
      <DeleteRoutineButton
        workspaceRoutine={routine}
        onSelected={() => navigate('/new-routines')}
      />
      <Button
        style={headerStyles.modifyButton}
        labelStyle={headerStyles.modifyLabel}
        onPress={() => navigate(`/new-routines/${routineId}/edit`)}
        buttonColor={Colors.primary}
      >
        {t('topNav.modify')}
      </Button>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  editContainer: {
    flex: 1,
    padding: 10,
  },
});

const headerStyles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.baseMargin.base,
  },
  modifyButton: {
    borderRadius: 8,
    maxWidth: 120,
    height: 36,
    justifyContent: 'center',
  },
  modifyLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.white,
  },
});

const UPDATE_ROUTINES_GROUPS = `
  mutation UpdateRoutinesGroups($input: UpdateRoutinesGroupsInput!) {
    routine {
      updateRoutinesGroups(input: $input) {
        success
        message
      }
    }
  }
`;
